import { secondAxios } from "../../../../../../api/axios";
import { resolvePlantIdForDocTransmittal } from "../Table/api";

/** @typedef {{ id: string, name: string }} ProjectChoice */

function normalizePlantProjectsPayload(resData) {
  const projectsRaw =
    resData?.projects ||
    (Array.isArray(resData?.project)
      ? resData.project
      : resData?.project
        ? [resData.project]
        : Array.isArray(resData)
          ? resData
          : []);
  return Array.isArray(projectsRaw) ? projectsRaw : [];
}

/**
 * Projects under a plant (for Document Register rows — not UUID Register scope).
 * @param {string} plantId
 * @returns {Promise<ProjectChoice[]>}
 */
export async function fetchPlantProjectChoices(plantId) {
  if (!plantId) return [];
  try {
    const { data } = await secondAxios.get(`/plant/${plantId}`);
    const raw = normalizePlantProjectsPayload(data);
    const client = process.env.REACT_APP_CLIENT_NAME;
    const filtered = client
      ? raw.filter((p) => p?.clientName === client)
      : raw;
    return filtered
      .map((p) => ({
        id: String(p?.id ?? p?.projectId ?? "").trim(),
        name: String(
          p?.name ?? p?.title ?? p?.projectName ?? "Unnamed project",
        ).trim(),
      }))
      .filter((p) => p.id);
  } catch {
    return [];
  }
}

/**
 * Resolve plant UUID for UUID Register (route plantId or project → plant).
 * @param {{ plant?: boolean, plantId?: string, activeProjectId?: string }} scope
 * @returns {Promise<string|null>}
 */
export async function resolveUuidRegisterPlantId(scope) {
  return resolvePlantIdForDocTransmittal(scope);
}

/**
 * Normalized row for the UUID Register table (Nest `UuidRegister`, plant-scoped).
 * @typedef {Object} UuidRegisterRow
 * @property {string} id
 * @property {string} [plantId]
 * @property {string} tag
 * @property {string} registerNumber
 * @property {string} uid
 * @property {string} [description]
 */

/** @param {object} raw */
export function mapUuidRegisterDto(raw) {
  if (!raw) return null;
  const archived =
    raw.archived === true ||
    raw.isArchived === true ||
    raw.status === "archived";
  return {
    id: raw.id,
    plantId: String(raw.plantId ?? raw.plant?.id ?? "").trim() || undefined,
    tag: raw.tag ?? "",
    registerNumber: String(
      raw.registerNumber ?? raw.register_number ?? "",
    ).trim(),
    uid: raw.uid ?? "",
    description: raw.description ?? "",
    archived,
  };
}

/** Unwrap list payloads from GET uuid-register endpoints. */
export function normalizeUuidRegisterList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  if (typeof data?.data === "string") {
    try {
      const parsed = JSON.parse(data.data);
      if (Array.isArray(parsed)) return parsed;
      if (Array.isArray(parsed?.items)) return parsed.items;
    } catch {
      /* ignore */
    }
  }
  return [];
}

/**
 * @param {string} plantId - Plant UUID (not register row id)
 * @returns {Promise<UuidRegisterRow[]>}
 */
export async function fetchUuidRegisterByPlantId(plantId) {
  if (!plantId) return [];
  const { data } = await secondAxios.get(`/uuid-register/plant/${plantId}`);
  const list = normalizeUuidRegisterList(data);
  return list.map(mapUuidRegisterDto).filter(Boolean);
}

/**
 * @param {string} plantId
 * @param {{ tag: string, registerNumber: string, description?: string }} body
 * @returns {Promise<UuidRegisterRow>}
 */
export async function createUuidRegisterForPlant(plantId, body) {
  const payload = {
    tag: String(body.tag ?? "").trim(),
    registerNumber: String(body.registerNumber ?? "").trim(),
  };
  const desc = body.description;
  if (desc !== undefined && desc !== null && String(desc).trim() !== "") {
    payload.description = String(desc).trim();
  }
  const { data } = await secondAxios.post(
    `/uuid-register/plant/${plantId}`,
    payload,
  );
  return data ? mapUuidRegisterDto(data) : null;
}

/**
 * @param {string} registerRowId - Register row UUID
 * @param {{ tag?: string, registerNumber?: string, number?: string, description?: string, archived?: boolean }} body
 * @returns {Promise<UuidRegisterRow|null>}
 */
export async function patchUuidRegister(registerRowId, body) {
  const dto = {};
  if (body.tag !== undefined) dto.tag = String(body.tag).trim();
  const rn = body.registerNumber ?? body.number;
  if (rn !== undefined) dto.registerNumber = String(rn).trim();
  if (body.description !== undefined) {
    dto.description =
      body.description === "" ? "" : String(body.description).trim();
  }
  if (body.archived !== undefined) dto.archived = Boolean(body.archived);
  const { data } = await secondAxios.patch(
    `/uuid-register/${registerRowId}`,
    dto,
  );
  return data ? mapUuidRegisterDto(data) : null;
}

/**
 * @param {string} registerRowId
 */
export async function deleteUuidRegisterById(registerRowId) {
  await secondAxios.delete(`/uuid-register/${registerRowId}`);
}

/**
 * @param {string} plantId
 * @param {string} uid - e.g. `V -101` (encoded via axios params)
 */
export async function deleteUuidRegisterByPlantAndUid(plantId, uid) {
  await secondAxios.delete(`/uuid-register/plant/${plantId}/by-uid`, {
    params: { uid },
  });
}

/** @returns {Promise<UuidRegisterRow[]>} */
export async function fetchAllUuidRegisters() {
  const { data } = await secondAxios.get("/uuid-register");
  const list = normalizeUuidRegisterList(data);
  return list.map(mapUuidRegisterDto).filter(Boolean);
}

/** @returns {Promise<UuidRegisterRow>} */
export async function fetchUuidRegisterById(registerRowId) {
  const { data } = await secondAxios.get(`/uuid-register/${registerRowId}`);
  return mapUuidRegisterDto(data);
}

/**
 * Load UUID register rows for the current DCS/plant or project route.
 * @param {{ plant?: boolean, plantId?: string, activeProjectId?: string }} scope
 * @returns {Promise<{ items: UuidRegisterRow[], plantId: string|null }>}
 */
export async function fetchUuidRegisterForScope({
  plant = false,
  plantId,
  activeProjectId,
} = {}) {
  const effectivePlantId = await resolveUuidRegisterPlantId({
    plant,
    plantId,
    activeProjectId,
  });

  if (!effectivePlantId) {
    return { items: [], plantId: null };
  }

  try {
    const rows = await fetchUuidRegisterByPlantId(effectivePlantId);
    const items = (rows || []).filter((row) => row && !row.archived);
    return { items, plantId: effectivePlantId };
  } catch (e) {
    console.warn("fetchUuidRegisterForScope failed:", e);
    return { items: [], plantId: effectivePlantId };
  }
}

/* --- Aliases matching previous module API names (plantId, not projectId) --- */

/** @deprecated Use fetchUuidRegisterByPlantId */
export async function fetchUuidRegisterByProjectId(plantId) {
  return fetchUuidRegisterByPlantId(plantId);
}

/** @deprecated Use createUuidRegisterForPlant */
export async function createUuidRegisterForProject(plantId, body) {
  return createUuidRegisterForPlant(plantId, body);
}

/** @deprecated Use deleteUuidRegisterByPlantAndUid */
export async function deleteUuidRegisterByProjectAndUid(plantId, uid) {
  return deleteUuidRegisterByPlantAndUid(plantId, uid);
}

export async function fetchUuidRegisterItems(plantId) {
  return fetchUuidRegisterByPlantId(plantId);
}

export async function createUuidRegisterItem(plantId, payload) {
  const registerNumberRaw =
    payload.registerNumber ?? payload.number ?? "";
  const registerNumber =
    registerNumberRaw !== null && registerNumberRaw !== undefined
      ? String(registerNumberRaw).trim()
      : "";
  return createUuidRegisterForPlant(plantId, {
    tag: payload.tag,
    registerNumber,
    description: payload.description,
  });
}

export async function updateUuidRegisterItem(payload) {
  const { id, tag, registerNumber, number, description } = payload;
  return patchUuidRegister(id, {
    tag,
    registerNumber,
    number,
    description,
  });
}

export async function deleteUuidRegisterItem(id) {
  return deleteUuidRegisterById(id);
}

/** Soft-archive: PATCH `archived: true` (no HTTP DELETE). */
export async function archiveUuidRegisterItem(id) {
  return patchUuidRegister(id, { archived: true });
}
